import axios from "axios";
import { Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import EditorTextArea from "../../../components/EditorTextArea";
import FileUploadImage from "../../../components/FileUploadImage";
import FileUploadVideo from "../../../components/FileUploadVideo";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { useLanguage } from "../../../context/LanguageContext";
import { BaseUrlApi, ErrorMessage } from "../../../lib/api";
import { CourseData } from "../../../lib/languages";
import DrawerAddLevels from "../Level/DrawerAddLevels";
import LevelsCard from "../Level/LevelsCard";
import TableGroups from "../Group/TableGroups";
import { useAPI } from "../../../context/APIContext";
import { Spin } from "antd";
import LoaderPage from "./../../../components/LoaderPage";
import { useAuth } from "../../../context/AuthContext";

export default function AdminViewCourses() {
  const { user } = useAuth();
  const { coursesItems, TeacherCoursesItems } = useAPI();
  const isAdmin = user?.role === "admin";

  // Use the appropriate API based on the user's role
  const loadingCourses = isAdmin
    ? coursesItems.loadingCourses
    : TeacherCoursesItems.loadingTeacherCourses;
  const courses = isAdmin
    ? coursesItems.courses
    : TeacherCoursesItems.teacherCourses;
  const getAllCourses = isAdmin
    ? coursesItems.getAllCourses
    : TeacherCoursesItems.getAllTeacherCourses;

  const { state } = useLocation();
  const subCategory = state?.subCategory;
  const selectedLevelId = state?.selectedLevelId;
  const { id } = useParams();
  // Filter courses for the current course
  const oneCourse = courses.filter((course) => course.id === +id);

  // ------------------------------------

  const { language } = useLanguage();
  const t = CourseData[language] || CourseData["en"];

  const [EditorRerender, setEditorRerender] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [image, setImage] = useState(null);
  const [bigImage, setBigImage] = useState(null);
  const [video, setVideo] = useState(null);
  const [form, setForm] = useState({
    title_en: undefined,
    title_ar: undefined,
    s_description_en: undefined,
    s_description_ar: undefined,
    b_description_en: undefined,
    b_description_ar: undefined,
    status: false,
    type: false,
  });

  useEffect(() => {
    setEditorRerender(!EditorRerender);
    setImage(oneCourse[0]?.image);
    setBigImage(oneCourse[0]?.big_image);
    setVideo(oneCourse[0]?.video);
    setForm({
      title_en: oneCourse[0]?.title?.en,
      title_ar: oneCourse[0]?.title?.ar,
      s_description_en: oneCourse[0]?.description?.en,
      s_description_ar: oneCourse[0]?.description?.ar,
      b_description_en: oneCourse[0]?.big_description?.en,
      b_description_ar: oneCourse[0]?.big_description?.ar,
      status: oneCourse[0]?.status,
      type: oneCourse[0]?.type,
    });
  }, [courses, id]);

  const handleEditCourse = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();

      if (form.title_ar || form.title_en) {
        formData.append(
          "title",
          JSON.stringify({ ar: form.title_ar, en: form.title_en })
        );
      }

      if (form.s_description_ar || form.s_description_en) {
        formData.append(
          "description",
          JSON.stringify({
            ar: form.s_description_ar,
            en: form.s_description_en,
          })
        );
      }

      if (form.b_description_ar || form.b_description_en) {
        formData.append(
          "big_description",
          JSON.stringify({
            ar: form.b_description_ar,
            en: form.b_description_en,
          })
        );
      }

      formData.append("image", image ?? "");
      formData.append("big_image", bigImage ?? "");
      formData.append("video", video ?? "");

      formData.append("status", form.status ? 1 : 0);
      formData.append("type", form.type ? 1 : 0);

      await axios.post(
        `${BaseUrlApi}/sub-categories/${subCategory.id}/courses/${oneCourse[0]?.bid}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast(t.courseUpdatedToast, {
        description: t.courseUpdatedDescription,
      });

      await getAllCourses();
      setEditMode(false);
    } catch (error) {
      setErrors(ErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (oneCourse[0]?.levels.length > 0) {
      setLevelId(selectedLevelId || oneCourse[0]?.levels[0].id);
    }
  }, [oneCourse[0]?.levels, selectedLevelId]);

  // Groups
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groups, setGroups] = useState([]);
  const [levelId, setLevelId] = useState(null);

  const getAllGroups = async () => {
    try {
      const { data } = await axios.get(
        `${BaseUrlApi}/levels/${levelId}/groups`
      );
      setGroups(data);
    } catch (error) {
      toast(t.error, {
        description: ErrorMessage(error),
      });
    } finally {
      setLoadingGroups(false);
    }
  };

  useEffect(() => {
    if (levelId) {
      setLoadingGroups(true);
      getAllGroups();
    }
  }, [levelId]);

  if (loadingCourses) {
    return <LoaderPage title={t.loadingCourses} />;
  }

  return (
    <div className="space-y-10" dir={language === "ar" ? "rtl" : "ltr"}>
      {/* Head */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="w-full sm:w-fit">
          <p className="font-bold text-2xl pt-5" id="course">
            {user?.role === "admin" ? t.updateCourse : t.viewCourse} “
            {oneCourse[0]?.title[language] ||
              oneCourse[0]?.title.en ||
              oneCourse[0]?.title.ar ||
              "---"}
            ”
          </p>
          <p className="font-medium text-sm">{t.easilyCreateAndManageCourse}</p>
        </div>
        {user?.role === "admin" && (
          <div className="flex items-center gap-2 w-full sm:w-fit">
            {editMode && (
              <Button
                className="w-full sm:w-fit h-10 bg-blue-600 text-white hover:bg-blue-600/80 rounded-xl"
                onClick={handleEditCourse}
                disabled={loading || !editMode}
              >
                {loading && (
                  <div className="animate-spin">
                    <Loader2 />
                  </div>
                )}
                {t.updateCourse}
              </Button>
            )}
            <Button
              variant="outline"
              className={`w-full sm:w-fit h-10 rounded-xl ${
                editMode
                  ? "border-2 border-blue-600 text-blue-600 hover:text-blue-500"
                  : "bg-blue-600 text-white hover:bg-blue-600/80 hover:text-white"
              }`}
              onClick={() => setEditMode(!editMode)}
              disabled={loading}
            >
              {editMode ? t.showMode : t.editMode}
            </Button>
          </div>
        )}
      </div>

      {/* Uploads */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 relative">
        {/* Upload Thumbnail */}
        <Card>
          <CardContent className="p-4 space-y-4 flex flex-col h-full">
            <p className="font-bold text-xl">{t.thumbnail}</p>
            <FileUploadImage
              onUploadComplete={(url) => setImage(url)}
              name="courses"
              image={image}
              disabled={!editMode}
            />
            {errors?.image && (
              <p className="text-red-500 font-semibold text-sm">
                {errors.image[0]}
              </p>
            )}
            <div className="font-normal text-sm text-muted-foreground text-center">
              {t.imageExtensionInfo}
            </div>
          </CardContent>
        </Card>
        {/* Upload Big Image */}
        <Card>
          <CardContent className="p-4 space-y-4 flex flex-col h-full">
            <p className="font-bold text-xl">{t.bigImage}</p>
            <FileUploadImage
              onUploadComplete={(url) => setBigImage(url)}
              name="courses"
              image={bigImage}
              disabled={!editMode}
            />
            {errors?.big_image && (
              <p className="text-red-500 font-semibold text-sm">
                {errors.big_image[0]}
              </p>
            )}
            <div className="font-normal text-sm text-muted-foreground text-center">
              {t.imageExtensionInfo}
            </div>
          </CardContent>
        </Card>
        {/* Upload Video */}
        <Card>
          <CardContent className="p-4 space-y-4 flex flex-col h-full">
            <p className="font-bold text-xl">{t.video}</p>
            <FileUploadVideo
              onUploadComplete={(url) => setVideo(url)}
              name="courses"
              video={video}
              disabled={!editMode}
            />
            {errors?.video && (
              <p className="text-red-500 font-semibold text-sm">
                {errors.video[0]}
              </p>
            )}
            <div className="font-normal text-sm text-muted-foreground text-center">
              {t.videoExtensionInfo}
            </div>
          </CardContent>
        </Card>
      </div>
      {/* General information */}
      <Card>
        <CardContent className="p-4 space-y-4 flex-1 flex flex-col">
          <p className="font-bold text-xl border-b pb-2">
            {t.generalInformation}
          </p>
          <div className="space-y-4 flex-1 flex flex-col">
            <div className="flex items-center gap-4">
              <div className="flex flex-col gap-2 w-full">
                <label htmlFor="title_en" className="text-sm">
                  {t.titleEn}
                </label>
                <Input
                  id="title_en"
                  name="title_en"
                  type="text"
                  placeholder={t.enterTitleEn}
                  onChange={(e) =>
                    setForm({ ...form, title_en: e.target.value })
                  }
                  value={form.title_en}
                  readOnly={!editMode}
                />
                {errors?.title && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.title[0]}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2 w-full">
                <label htmlFor="title_ar" className="text-sm">
                  {t.titleAr}
                </label>
                <Input
                  id="title_ar"
                  name="title_ar"
                  type="text"
                  placeholder={t.enterTitleAr}
                  onChange={(e) =>
                    setForm({ ...form, title_ar: e.target.value })
                  }
                  value={form.title_ar}
                  readOnly={!editMode}
                />
                {errors?.title && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.title[0]}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Small Description */}
              <div className="flex flex-col gap-2 flex-1">
                <label htmlFor="description" className="text-sm">
                  {t.smallDescription}
                </label>
                <EditorTextArea
                  EditorRerender={EditorRerender}
                  form={form}
                  setForm={setForm}
                  en="s_description_en"
                  ar="s_description_ar"
                  disabled={!editMode}
                />
              </div>
              {/* Big Description */}
              <div className="flex flex-col gap-2 flex-1">
                <label htmlFor="description" className="text-sm">
                  {t.bigDescription}
                </label>
                <EditorTextArea
                  EditorRerender={EditorRerender}
                  form={form}
                  setForm={setForm}
                  en="b_description_en"
                  ar="b_description_ar"
                  disabled={!editMode}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      {/* Conditions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Status */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center gap-4 justify-between border-b pb-2">
              <p className="font-bold text-xl">{t.status}</p>
              <div
                className={`size-4 ${
                  !!form?.status ? "bg-green-500" : "bg-red-500"
                } rounded-full`}
              />
            </div>
            <div className="grid gap-2">
              <p className="text-sm">{t.selectStatus}</p>
              <Select
                disabled={!editMode}
                value={!!form?.status}
                onValueChange={(e) => setForm({ ...form, status: e })}
              >
                <SelectTrigger
                  className="rounded-lg sm:ml-auto"
                  aria-label={t.selectStatus}
                >
                  <SelectValue placeholder={t.selectStatus} />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value={true} className="rounded-lg">
                    {t.active}
                  </SelectItem>
                  <SelectItem value={false} className="rounded-lg">
                    {t.inactive}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
        {/* Type */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center gap-4 justify-between border-b pb-2">
              <p className="font-bold text-xl">{t.selectType}</p>
              <div
                className={`relative size-4 ${
                  !!form?.type ? "bg-red-500" : "bg-green-500"
                } rounded-full`}
              >
                {!!form?.type && (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                )}
              </div>
            </div>
            <div className="grid gap-2">
              <p className="text-sm">{t.selectType}</p>
              <Select
                disabled={!editMode}
                value={!!form?.type}
                onValueChange={(e) => setForm({ ...form, type: e })}
              >
                <SelectTrigger
                  className="rounded-lg sm:ml-auto"
                  aria-label={t.selectType}
                >
                  <SelectValue placeholder={t.selectType} />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value={true} className="rounded-lg">
                    {t.live}
                  </SelectItem>
                  <SelectItem value={false} className="rounded-lg">
                    {t.recorded}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Levels */}
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl font-bold">{t.levels}</h1>
          {user?.role === "admin" && (
            <DrawerAddLevels getAllLevels={getAllCourses} item={oneCourse[0]} />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {oneCourse[0]?.levels?.length !== 0
            ? oneCourse[0]?.levels?.map((level, index) => (
                <LevelsCard
                  key={index}
                  level={level}
                  course={oneCourse[0]}
                  setLevelId={setLevelId}
                  levelId={levelId}
                  getAllLevels={getAllCourses}
                />
              ))
            : "No Results"}
        </div>
      </div>

      {/* Groups */}
      <div className="">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl font-bold" id={`lessons`}>
            {t.studyEnroll}
          </h1>
          {/* <DrawerAddGroup getAllGroups={getAllGroups} levelId={levelId} /> */}
        </div>

        {!loadingGroups ? (
          <TableGroups
            t={t}
            data={groups}
            levels={oneCourse[0]?.levels}
            getAllGroups={getAllGroups}
          />
        ) : (
          <div className="h-40 w-full flex items-center justify-center">
            <Spin size="large" />
          </div>
        )}
      </div>
    </div>
  );
}
